// Datei: src/components/messages/MessagesPage.tsx
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getMessages, Conversation, deleteConversation } from '../../utils/api';
import { useAuth } from '../auth/AuthContext';
import { Trash2 } from 'lucide-react';

const MessagesPage = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingIds, setDeletingIds] = useState<number[]>([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const loadConversations = async () => {
      try {
        console.log('Lade alle Konversationen');
        const response = await getMessages(0); // 0 = alle Konversationen
        console.log('Konversationen-Response:', response);
        
        if (response.success && response.data) {
          setConversations(response.data);
        } else {
          console.error('Fehler beim Laden der Konversationen:', response);
        }
      } catch (err) {
        console.error('Fehler beim Laden der Nachrichten:', err);
      } finally {
        setLoading(false);
      }
    };

    loadConversations();
  }, [user, navigate]);

  const handleDeleteConversation = async (productId: number, userId: number, e: React.MouseEvent) => {
    e.preventDefault(); // Verhindert, dass der Link zum Produkt navigiert
    e.stopPropagation(); // Verhindert Event-Bubbling
    
    if (!window.confirm('Möchten Sie diese Konversation wirklich löschen?')) {
      return;
    }
    
    setDeletingIds(prev => [...prev, productId]);
    
    try {
      const response = await deleteConversation(productId, userId);
      
      if (response.success) {
        // Entferne die gelöschte Konversation aus der Liste
        setConversations(prev => prev.filter(conv => 
          !(conv.product_id === productId && conv.other_user_id === userId)
        ));
      } else {
        alert('Fehler beim Löschen der Konversation: ' + response.message);
      }
    } catch (err) {
      console.error('Fehler beim Löschen der Konversation:', err);
      alert('Fehler beim Löschen der Konversation');
    } finally {
      setDeletingIds(prev => prev.filter(id => id !== productId));
    }
  };

  if (loading) {
    return <div className="container mx-auto px-4 py-8">Nachrichten werden geladen...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Meine Nachrichten</h1>

      {conversations.length === 0 ? (
        <div className="bg-gray-50 p-8 rounded-lg text-center">
          <p className="text-gray-600 mb-4">Du hast noch keine Nachrichten.</p>
          <Link to="/" className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600">
            Produkte entdecken
          </Link>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md">
          {conversations.map((conversation) => (
            <div
              key={`${conversation.product_id}-${conversation.other_user_id}`}
              className="relative border-b last:border-b-0"
            >
              <Link
                to={`/products/${conversation.product_id}?chat=true&user_id=${conversation.other_user_id}`}
                className="flex items-center p-4 hover:bg-gray-50"
              >
                <div className="flex items-center flex-1">
                  <div 
                    className="w-16 h-16 rounded overflow-hidden bg-gray-200 mr-4"
                    style={{
                      backgroundImage: `url(${conversation.product_image || '/placeholder-image.jpg'})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  />
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <h3 className="font-semibold text-gray-800">{conversation.product_title}</h3>
                      <span className="text-xs text-gray-500 mr-8">
                        {new Date(conversation.last_message_time).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-gray-600">Kontakt mit {conversation.other_user_name}</p>
                    {conversation.unread_count > 0 && (
                      <span className="inline-block bg-orange-500 text-white text-xs px-2 py-1 rounded-full mt-1">
                        {conversation.unread_count} neue {conversation.unread_count === 1 ? 'Nachricht' : 'Nachrichten'}
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Löschbutton - jetzt rechts außen positioniert */}
                <button
                  onClick={(e) => handleDeleteConversation(conversation.product_id, conversation.other_user_id, e)}
                  disabled={deletingIds.includes(conversation.product_id)}
                  className="text-red-500 hover:text-red-700 bg-red-100 p-2 rounded-full ml-2"
                  title="Konversation löschen"
                >
                  <Trash2 size={16} />
                </button>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MessagesPage;